import type {
  ModelTreeItem,
  ModelTreeProps,
  OnExpandChangeProps,
} from "./model-tree.type";
import type { Key } from "react";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { getModelLink } from "@/modules/model";

import { getModelTreeItems } from "./model-tree.helper";

export const useModelTree = ({ nodes, saveStateKey }: ModelTreeProps) => {
  const navigate = useNavigate();

  const localStorageKey = saveStateKey
    ? `${saveStateKey}-opened-keys`
    : undefined;

  const [openedKeys, setOpenedKeys] = useState<Key[]>([]);
  const treeItems = getModelTreeItems({ nodes, parent: null });

  const defineOpenedKeysOnLoad = () => {
    if (!localStorageKey) return;

    const savedKeys = localStorage.getItem(localStorageKey);
    if (!savedKeys) return;

    setOpenedKeys(JSON.parse(savedKeys));
  };

  useEffect(() => {
    defineOpenedKeysOnLoad();

    return () => {
      setOpenedKeys([]);
    };
  }, []);

  const onItemExpandChange = ({
    item,
    isExpanded,
    expandedKeys,
  }: OnExpandChangeProps) => {
    const newKeys = isExpanded
      ? expandedKeys
      : expandedKeys.filter((key) => key !== item.key);

    setOpenedKeys(newKeys);

    if (localStorageKey) {
      localStorage.setItem(localStorageKey, JSON.stringify(newKeys));
    }
  };

  const onSelectItem = ({ data }: ModelTreeItem) => {
    const link = getModelLink(data.type, data);
    if (link) navigate(link);
  };

  return {
    treeItems,
    openedKeys,
    onItemExpandChange,
    onSelectItem,
  };
};
